import { format } from "date-fns";

/**
 * Formats a given date string or Date object into a readable string.
 *
 * This function safely formats a date, applying the given format string.
 * If the date is invalid or not provided, it returns a fallback string instead of throwing an error.
 * By default, the date is formatted in the user's local timezone. If useUTC is true, the date is formatted in UTC.
 *
 * @param date - The date to format. Can be a string, Date object, or undefined/null.
 * @param formatStr - (Optional) The date format string, defaulting to `"dd/MM/yyyy HH:mm"`.
 * @param fallback - (Optional) The fallback string to return if the date is invalid. Defaults to an empty string.
 * @param useUTC - (Optional) If true, formats the date in UTC. If false or omitted, formats in local time.
 *
 * @returns A formatted date string, or the fallback string if formatting fails.
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  formatStr: string = "dd/MM/yyyy HH:mm",
  fallback: string = "",
  useUTC: boolean = false
): string => {
  if (!date) return fallback;
  try {
    const d = new Date(date);
    if (useUTC) {
      // Manually build the string in UTC
      const pad = (n: number) => n.toString().padStart(2, "0");
      const day = pad(d.getUTCDate());
      const month = pad(d.getUTCMonth() + 1);
      const year = d.getUTCFullYear();
      const hours = pad(d.getUTCHours());
      const minutes = pad(d.getUTCMinutes());
      // Only support default format for UTC for now
      if (formatStr === "dd/MM/yyyy HH:mm") {
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      }
      // For custom formats, fallback to ISO string
      return d.toISOString();
    } else {
      return format(d, formatStr);
    }
  } catch (e) {
    console.warn("Invalid date:", date);
    return fallback;
  }
};

/**
 * Rounds a date to a specified minute interval.
 *
 * @param date - The date to round. Defaults to the current date/time.
 * @param interval - The interval (in minutes) to round to. Defaults to 15.
 * @param roundUp - If true, rounds up to the next interval (stays the same if already on an interval);
 *                  if false, rounds to the nearest interval.
 * @returns A new Date object rounded according to the given interval and rounding mode.
 */
export const roundTimeToInterval = (date?: Date, interval: number = 15, roundUp: boolean = false): Date => {
  if (interval <= 0) throw new Error("Interval must be greater than 0");

  const targetDate = date ?? new Date();
  const rounded = new Date(targetDate);

  const minutes = rounded.getMinutes();
  const seconds = rounded.getSeconds();

  if (roundUp) {
    const remainder = minutes % interval;
    const add = remainder > 0 ? interval - remainder : 0;
    rounded.setMinutes(minutes + add, 0, 0);
  } else {
    const totalMinutes = minutes + seconds / 60;
    const nearest = Math.round(totalMinutes / interval) * interval;
    rounded.setMinutes(nearest, 0, 0);
  }

  return rounded;
};

/**
 * Formats minutes into hours:minutes.
 *
 * @param totalMinutes - The amount of minutes to format.
 * @returns A formatted value of hours:minutes, if 0 or null then empty string.
 */
export const formatMinutes = (totalMinutes?: number | null): string => {
  if (!totalMinutes) return "";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};
